import {
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
} from "@floating-ui/react";
import { clsx } from "clsx";
import { ReactNode, useEffect, useState } from "react";
import { z } from "zod";

import { useStorage } from "@smart/itops-hooks-dom";

import { CustomDateRangePicker } from "./custom-date-range-picker";
import { Button } from "../button";
import { BaseInput } from "../input/base";
import { RadioButtons } from "../radio";
import { formatDateRange, applyFromDate } from "../shared/utils";

export type TimePeriod =
  | "allTime"
  | "today"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "customDates";

export type TimeRange = {
  from?: Date;
  to?: Date;
};

export const filterLabel: Record<TimePeriod, string> = {
  allTime: "All Time",
  today: "Today",
  thisWeek: "This Week",
  lastWeek: "Last Week",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  customDates: "Custom",
};

export const defaultFilters: TimePeriod[] = [
  "allTime",
  "today",
  "thisWeek",
  "lastWeek",
  "thisMonth",
  "lastMonth",
  "customDates",
];

type FilterOption = {
  value: TimePeriod;
  label: string;
  detail?: ReactNode;
  className?: string;
};

type FilterDropdownProps = {
  filters?: TimePeriod[];
  defaultFilter?: TimePeriod;
  isButton?: boolean;
  onChange: (period: TimePeriod, range?: TimeRange) => void;
  filterStorageKey: string;
  disabled?: boolean;
  dataTestId?: string;
  compact?: boolean;
  customDatesFilter?: boolean;
  unrestricted?: boolean;
};

export const TimeFilter = ({
  filters = defaultFilters,
  defaultFilter = "thisWeek",
  isButton,
  onChange,
  filterStorageKey,
  disabled,
  dataTestId,
  compact,
  unrestricted,
}: FilterDropdownProps) => {
  const [currentFilter, setCurrentFilter] = useStorage<TimePeriod>({
    defaultValue: defaultFilter,
    key: filterStorageKey,
    schema: z.enum([
      "allTime",
      "today",
      "thisWeek",
      "lastWeek",
      "thisMonth",
      "lastMonth",
      "customDates",
    ]),
    storage: "local",
  });

  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [dateRange, setDateRange] = useStorage<TimeRange>({
    defaultValue: { from: oneMonthAgo, to: today },
    key: `${filterStorageKey}-range`,
    schema: z.object({
      from: z.coerce.date(),
      to: z.coerce.date(),
    }),
    storage: "local",
  });

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (currentFilter === "customDates") {
      onChange("customDates", dateRange);
    } else {
      onChange(currentFilter);
    }
  }, [currentFilter, dateRange]);

  const filtersToDisplay: FilterOption[] = filters.map((filter) => {
    const base = {
      label: filterLabel[filter],
      value: filter,
    };
    return {
      ...base,
      ...(filter === "customDates"
        ? {
            labelOverride: (
              <CustomDateRangePicker
                onClick={() => {
                  if (currentFilter !== "customDates")
                    setCurrentFilter("customDates");
                }}
                onFromDateChange={(date) =>
                  setDateRange(applyFromDate(dateRange, date))
                }
                selectedFromDate={dateRange?.from}
                onToDateChange={(date) =>
                  setDateRange({ ...dateRange, to: date })
                }
                selectedToDate={dateRange?.to}
                unrestricted={unrestricted}
              />
            ),
          }
        : {}),
    };
  });

  const options = useFloating({
    placement: "bottom-start",
    open,
    onOpenChange: setOpen,
    middleware: [
      offset({ mainAxis: 4, crossAxis: -4 }),
      flip({ fallbackPlacements: ["top-start"] }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const optionsInteractions = useInteractions([
    useDismiss(options.context),
    useFocus(options.context),
  ]);

  const isCustomDates = currentFilter === "customDates";
  return (
    <div
      ref={options.refs.setReference}
      {...optionsInteractions.getReferenceProps()}
      className="flex flex-col"
    >
      {isButton ? (
        <Button
          variant="secondarySubtle"
          rightIcon="regularAngleDown"
          text={
            isCustomDates
              ? formatDateRange(dateRange)
              : filterLabel[currentFilter]
          }
          onClick={() => setOpen(!open)}
          disabled={disabled}
          dataTestId={dataTestId}
        />
      ) : (
        <BaseInput
          value={
            isCustomDates
              ? formatDateRange(dateRange)
              : filterLabel[currentFilter]
          }
          onRightButtonClick={() => setOpen(!open)}
          rightButtonIcon="regularAngleDown"
          readOnly
          disabled={disabled}
          dataTestId={dataTestId}
          compact={compact}
          className={clsx(isCustomDates && "w-[24rem]")}
        />
      )}
      {open && (
        <div
          ref={options.refs.setFloating}
          style={options.floatingStyles}
          {...optionsInteractions.getFloatingProps()}
          className={clsx(
            "flex flex-col z-modal rounded bg-white p-[1.6rem] gap-small shadow-dropdownMenu",
            filtersToDisplay.some((f) => f.value === "customDates")
              ? "w-[25rem]"
              : "w-[21.2rem]",
          )}
        >
          <RadioButtons
            className="text-ui"
            options={filtersToDisplay}
            value={currentFilter}
            onChange={(value) => {
              setCurrentFilter(value as TimePeriod);
              if (value !== "customDates") setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export { CustomDatePicker } from "./custom-date-picker";
